import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight, CalendarDays, ListChecks, Plus, Rocket } from "lucide-react";
import Header from "../../components/Header/Header";
import ArrowButton from "../../components/ArrowButton/ArrowButton";
import { useInView } from "../../util/useInView";
import UniversidadeForm from "./UniversidadeForm";
import { PageSeo } from "../../seo/Seo";
import { BACK_TO_SITE, FAQ, HEADER_CTA, HEADER_LINKS, HIGHLIGHTS, RULES, STEPS, TIMELINE } from "./UniversidadeContent";
import { SITE_URL } from "../../seo/site";
import heroPhoto from "../../assets/redesign/universidade/hero-aluno-campus.jpg";
import brasao from "../../assets/redesign/universidade/brasao-universidade.webp";
import "./UniversidadeCalriz.css";

const pad = (n: number) => String(n + 1).padStart(2, "0");
const reveal = (i: number) => ({ "data-reveal": true, style: { "--i": i } as React.CSSProperties });

function SectionHead({ id, eyebrow, title, text, onDark }: { id: string; eyebrow: string; title: React.ReactNode; text?: string; onDark?: boolean }) {
  return (
    <div className="cz-uni-head">
      <span className={`cz-eyebrow${onDark ? " cz-eyebrow--on-dark" : ""}`}>{eyebrow}</span>
      <h2 className="cz-uni-h2" id={id}>
        {title}
      </h2>
      {text && <p className="cz-uni-lead">{text}</p>}
    </div>
  );
}

// Hero sem a grade da home: uma trilha pontilhada corre pelo fundo e, à direita, o aluno na frente da fachada da
// universidade. A foto já vem composta (cor, desfoque e bordas esmaecidas) e apoia na base do hero, com a parte de
// baixo escondida atrás do cartão de destaques.
function Hero() {
  return (
    <>
      <section className="cz-uni-hero" aria-labelledby="cz-uni-hero-title">
        <svg className="cz-uni-hero__trail" viewBox="0 0 1440 720" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
          <path className="cz-uni-hero__trail-line" d="M-40 640 C 240 600, 420 700, 640 600 S 980 420, 1120 470 S 1380 260, 1480 140" />
          <path className="cz-uni-hero__trail-dots" d="M-40 640 C 240 600, 420 700, 640 600 S 980 420, 1120 470 S 1380 260, 1480 140" />
          <circle cx="640" cy="600" r="7" />
          <circle cx="1120" cy="470" r="7" />
        </svg>

        <div className="cz-uni-hero__inner">
          <div className="cz-uni-hero__content">
            
            <h1 className="cz-uni-hero__title" id="cz-uni-hero-title">
              Domine tecnologias de ponta, conquiste sua certificação e{" "}
              <span className="cz-uni-accent">receba um incentivo financeiro.</span>
            </h1>
            <p className="cz-uni-hero__text">
              A Calriz University une aprendizado prático, vouchers de certificação 100% gratuitos para os melhores
              desempenhos, bonificação por vínculo de certificação e acesso direto ao nosso programa de estágio.
            </p>
            <div className="cz-uni-hero__actions">
              <ArrowButton to="#inscricao" variant="pink" size="md" className="cz-uni-hero__cta">
                Quero me inscrever gratuitamente
              </ArrowButton>
              <Link to="#como-funciona" className="cz-outline-btn cz-uni-hero__secondary">
                Como funciona
                <ArrowDown size={16} aria-hidden="true" />
              </Link>
            </div>
          </div>

          <div className="cz-uni-hero__visual">
            <img
              className="cz-uni-hero__photo"
              src={heroPhoto}
              alt="Estudante com a jaqueta da Calriz University na frente da universidade"
              width={1300}
              height={1130}
            />
          </div>
        </div>
      </section>

      <div className="cz-uni-highlights">
        <ul className="cz-uni-highlights__box">
          {HIGHLIGHTS.map(({ key, Icon, title, text }) => (
            <li key={key} className="cz-uni-highlights__item">
              <span className="cz-uni-highlights__icon" aria-hidden="true">
                <Icon size={22} strokeWidth={1.7} />
              </span>
              <span className="cz-uni-highlights__text">
                <strong>{title}</strong>
                {text}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}

// Como funciona: 5 passos ligados por uma linha que corre de um a outro; o último (carreira) é o destino, em destaque.
function Journey() {
  const [ref, state] = useInView<HTMLOListElement>(0.25);

  return (
    <section className="cz-uni-section cz-uni-journey" id="como-funciona" aria-labelledby="cz-uni-journey-title">
      <SectionHead
        id="cz-uni-journey-title"
        eyebrow="Como funciona"
        title={
          <>
            Sua jornada rumo ao topo do mercado de tecnologia em{" "}
            <span className="cz-uni-accent">5 passos simples</span>
          </>
        }
        text="Desenvolvemos um processo estruturado para selecionar, capacitar e premiar os talentos mais engajados."
      />
      <ol ref={ref} className={`cz-uni-steps${state}`} style={{ "--count": STEPS.length } as React.CSSProperties}>
        {STEPS.map(({ Icon, title, text }, i) => (
          <li key={title} className="cz-uni-step" style={{ "--i": i } as React.CSSProperties}>
            <span className="cz-uni-step__node" aria-hidden="true">{i + 1}</span>
            <div className="cz-uni-step__card">
              <span className="cz-uni-step__icon" aria-hidden="true">
                <Icon size={22} strokeWidth={1.7} />
              </span>
              <span className="cz-uni-step__label">Passo {i + 1}</span>
              <h3 className="cz-uni-step__title">{title}</h3>
              <p className="cz-uni-step__text">{text}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// Regras e benefícios: cards de vidro sobre a faixa escura.
function Rules() {
  const [ref, state] = useInView<HTMLUListElement>(0.2);

  return (
    <section className="cz-uni-rules" id="regras" aria-labelledby="cz-uni-rules-title">
      <span className="cz-uni-rules__orb cz-uni-rules__orb--pink" aria-hidden="true" />
      <span className="cz-uni-rules__orb cz-uni-rules__orb--lilac" aria-hidden="true" />
      <div className="cz-uni-section">
        <SectionHead
          id="cz-uni-rules-title"
          eyebrow="Regras e benefícios"
          title="Entenda as etapas do programa"
          text="Critérios claros do primeiro acesso à vaga: veja como cada etapa funciona e o que você conquista nela."
          onDark
        />
        <ul ref={ref} className={`cz-uni-rules__grid${state}`}>
          {RULES.map(({ Icon, title, text, badge }, i) => (
            <li key={title} className="cz-uni-rule" {...reveal(i)}>
              <span className="cz-outline-num cz-uni-rule__num" aria-hidden="true">{pad(i)}</span>
              <h3 className="cz-uni-rule__title">{title}</h3>
              <p className="cz-uni-rule__text">{text}</p>
              <span className="cz-uni-rule__badge">
                <span className="cz-uni-rule__badge-icon" aria-hidden="true">
                  <Icon size={13} strokeWidth={2.2} />
                </span>
                {badge.label}: <strong>{badge.value}</strong>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// Linha do tempo: etapas alternando dos dois lados de uma linha vertical, com o número da etapa no lado livre (no
// celular, todas à direita da linha). Mesma entrada dos passos: a linha desce de nó em nó e cada card aparece quando
// ela chega.
function Timeline() {
  const [ref, state] = useInView<HTMLOListElement>(0.2);

  return (
    <section className="cz-uni-section cz-uni-timeline" id="linha-do-tempo" aria-labelledby="cz-uni-timeline-title">
      <SectionHead
        id="cz-uni-timeline-title"
        eyebrow="Linha do tempo"
        title={
          <>
            Datas e regras do <span className="cz-uni-accent">Programa Calriz</span>
          </>
        }
        text="Do treinamento ao estágio: o período de cada etapa e o que você precisa cumprir nela."
      />
      <ol ref={ref} className={`cz-uni-timeline__list${state}`}>
        {TIMELINE.map(({ Icon, title, period, rule }, i) => (
          <li key={title} className="cz-uni-tl" style={{ "--i": i } as React.CSSProperties}>
            <span className="cz-uni-tl__node" aria-hidden="true">
              <Icon size={20} strokeWidth={1.8} />
            </span>
            <span className="cz-outline-num cz-uni-tl__num" aria-hidden="true">{pad(i)}</span>
            <div className="cz-uni-tl__card">
              <h3 className="cz-uni-tl__title">{title}</h3>
              {period && (
                <div className="cz-uni-tl__fact cz-uni-tl__fact--period">
                  <span className="cz-uni-tl__fact-icon" aria-hidden="true">
                    <CalendarDays size={17} strokeWidth={1.9} />
                  </span>
                  <p>
                    <span className="cz-uni-tl__fact-label">Período</span> {period}
                  </p>
                </div>
              )}
              {rule && (
                <div className="cz-uni-tl__fact">
                  <span className="cz-uni-tl__fact-icon" aria-hidden="true">
                    <ListChecks size={17} strokeWidth={1.9} />
                  </span>
                  <p>
                    <span className="cz-uni-tl__fact-label">Regra</span> {rule}
                  </p>
                </div>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

// Perguntas frequentes em acordeão: uma aberta por vez.
function Faq() {
  const [open, setOpen] = useState<number | null>(0);

  return (
    <section className="cz-uni-section cz-uni-faq" id="duvidas" aria-labelledby="cz-uni-faq-title">
      <div className="cz-uni-faq__aside">
        <SectionHead
          id="cz-uni-faq-title"
          eyebrow="Dúvidas"
          title="Perguntas frequentes"
          text="Ainda ficou com alguma dúvida sobre o programa? Nosso time responde."
        />
        <Link to="/contato" className="cz-outline-btn cz-uni-faq__help">
          Suporte e contato
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
      </div>

      <ul className="cz-uni-faq__list">
        {FAQ.map(({ q, a }, i) => {
          const isOpen = open === i;
          return (
            <li key={q} className={`cz-uni-faq__item${isOpen ? " is-open" : ""}`}>
              <h3>
                <button
                  type="button"
                  className="cz-uni-faq__q"
                  id={`cz-uni-faq-q${i}`}
                  aria-expanded={isOpen}
                  aria-controls={`cz-uni-faq-a${i}`}
                  onClick={() => setOpen(isOpen ? null : i)}
                >
                  {q}
                  <span className="cz-uni-faq__toggle" aria-hidden="true">
                    <Plus size={18} strokeWidth={2} />
                  </span>
                </button>
              </h3>
              <div className="cz-uni-faq__panel" id={`cz-uni-faq-a${i}`} role="region" aria-labelledby={`cz-uni-faq-q${i}`}>
                <div>
                  <p className="cz-uni-faq__a">{a}</p>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// Fim da página: link grande para o site institucional, na mesma aba e com endereço completo (a página
// é servida em calriz.org, onde "/" não é a home do site).
function BackToSite() {
  return (
    <section className="cz-uni-back" aria-label={BACK_TO_SITE}>
      <div className="cz-uni-back__inner">
        <a className="cz-uni-back__link" href={SITE_URL}>
          {BACK_TO_SITE}
          <span className="cz-uni-back__circle" aria-hidden="true">
            <ArrowRight strokeWidth={1.6} />
          </span>
        </a>
      </div>
    </section>
  );
}

// /universidade-calriz — landing da Calriz University (conteúdo em public/universidadeCalriz.docx). Títulos, botões e
// cards seguem a home e a página Sobre; o hero tem fundo próprio (trilha + aluno) em vez da grade.
export default function UniversidadeCalriz() {
  return (
    <div className="cz-page cz-scope cz-uni-page">
      <PageSeo page="universidade" path="/universidade-calriz" />
      <Header
        brand={{ logo: brasao, alt: "Universidade Calriz", to: "/universidade-calriz" }}
        links={HEADER_LINKS}
        cta={HEADER_CTA}
        autoHide
        hideLanguage
      />
      <main>
        <Hero />
        <Journey />
        <Rules />
        <Timeline />
        <Faq />
        <UniversidadeForm />
        <BackToSite />
      </main>
    </div>
  );
}
